import axios, { AxiosInstance } from "axios";
import { recordFailedRequest } from "../utils/clientContext";
import {
  GetUserNotificationsPayload,
  GetUserNotificationsResponse,
  VoiceTokenResponse,
  WhatsAppCallPermissionPayload,
  WhatsAppCallPermissionResponse,
  MarkNotificationAsReadPayload,
  RemoveNotificationsPayload,
  LoginResponse,
  OAuthLoginPayload,
  GetPatientsIntakePayload,
  GetPatientsIntakeResponse,
  SearchPatientsPayload,
  SearchPatientsResponse,
  GetClinicsResponse,
  User,
  OAuthLoginApiResponse,
  AuthMeApiResponse,
  ClinicApiResponse,
  Setup2FAResponse,
  GetMeetingsByDatesPayload,
  MeetingsByDatesResponse,
  UpdateMeetingStatusPayload,
  UpdateMeetingStatusResponse,
  GetUsersPayload,
  GetUsersResponse,
  CreateUserPayload,
  CreateUserResponse,
  UpdateUserPayload,
  UpdateUserResponse,
  GetMeetingTypesResponse,
  ListView,
  CreateListViewPayload,
  UpdateListViewPayload,
  GetListViewsResponse,
  GetPatientsTasksPayload,
  GetPatientsTasksResponse,
  GetPatientMeetingsResponse,
  PatientMeeting,
  MeetingSummary,
  GetMeetingSummaryPayload,
  UpdateMeetingSummaryPayload,
  GetMeetingSummaryPDFPayload,
  PDFResponse,
  GetDiagnosisPayload,
  GetDiagnosisResponse,
  GetMedicationTypesPayload,
  GetMedicationTypesResponse,
  CMMeetingSummary,
  GetCMMeetingSummaryPayload,
  UpdateCMMeetingSummaryPayload,
  UpdateCMMeetingPayload,
  UpdateCMMeetingResponse,
  PSYMeetingInsights,
  GetPSYMeetingInsightsPayload,
  UpdatePSYMeetingPayload,
  UpdatePSYMeetingResponse,
  SavePSYMeetingPayload,
  SavePSYMeetingResponse,
  PSYMeetingAgenda,
  GetPSYMeetingAgendaPayload,
  GetPatientMoodStatusPayload,
  GetPatientMoodStatusResponse,
  FinishMeetingPayload,
  FinishMeetingResponse,
  RunTestPromptPayload,
  RunTestPromptResponse,
  GetPatientPagePayload,
  GetPatientPageResponse,
  UpdatePatientSuicidalQuestionnairePayload,
  UpdatePatientSuicidalQuestionnaireResponse,
  UpdateFinishStatusPayload,
  UpdateFinishStatusResponse,
  UpdateObligationPayload,
  UpdateObligationResponse,
  MovePatientPayload,
  MovePatientResponse,
  GetPatientFilesPayload,
  GetPatientFilesResponse,
  GetPatientFileTypesPayload,
  GetPatientFileTypesResponse,
  UpdatePatientFilePayload,
  UpdatePatientFileResponse,
  PatientUploadFilePayload,
  PatientUploadFileResponse,
  UploadFilesRequestPayload,
  UploadFilesRequestResponse,
  GetUploadFileTypesPayload,
  GetUploadFileTypesResponse,
  GetPatientQuestionnairesPayload,
  GetPatientQuestionnairesResponse,
  GetQuestionnaireDetailsPayload,
  GetQuestionnaireDetailsResponse,
  GetPatientSuicidalQuestHistoryPayload,
  GetPatientSuicidalQuestHistoryResponse,
  ChangePasswordPayload,
  ChangePasswordResponse,
  CheckForgotPasswordKeyPayload,
  CheckForgotPasswordKeyResponse,
  UpdateForgotPasswordPayload,
  UpdateForgotPasswordResponse,
  UploadTranscriptPayload,
  UploadTranscriptResponse,
  TaskType,
  CreateInternalTaskPayload,
  CreateInternalTaskResponse,
  GetInternalTasksPayload,
  GetInternalTasksResponse,
  GetInternalTaskByIdPayload,
  InternalTaskItem,
  UpdateInternalTaskPayload,
  UpdateInternalTaskResponse,
  DeleteInternalTaskPayload,
  GetNotesPayload,
  GetNotesResponse,
  CreateNotePayload,
  CreateNoteResponse,
  UpdateNotePayload,
  UpdateNoteResponse,
  GetTaskHistoryPayload,
  GetTaskHistoryResponse,
  FinishApprovalData,
  FinishApprovalPayload,
  FinishApprovalResponse,
  UpdateGuardianStatusPayload,
  UpdateGuardianStatusResponse,
  UpdateUploadFilesContactedPayload,
  UpdateUploadFilesContactedResponse,
  GetWhatsAppMessagesPayload,
  GetWhatsAppMessagesResponse,
  SendWhatsAppMessagePayload,
  SendWhatsAppMessageResponse,
  GetWhatsAppConversationsPayload,
  GetWhatsAppConversationsResponse,
  MarkWhatsAppReadPayload,
  WhatsAppTemplate,
  WhatsAppConversation,
  AssignWhatsAppConversationPayload,
  UpdateWhatsAppConversationStatusPayload,
  CreateMedicalOpinionPayload,
  CreateMedicalOpinionResponse,
  GetMedicalOpinionsPayload,
  GetMedicalOpinionsResponse,
  GetMedicalOpinionPayload,
  GetMedicalOpinionResponse,
  GetMedicalOpinionFilePayload,
  GetMedicalOpinionFileResponse,
  MedicalOpinionObligationResponsePayload,
  MedicalOpinionStatusResponse,
  GetMedicalOpinionTypesResponse,
  GetMedicalOpinionCloseReasonsResponse,
  GetMedicalOpinionPsyDataPayload,
  MedicalOpinionPsyData,
  MedicalOpinionPsyContentPayload,
  MedicalOpinionPsyResponsePayload,
  MedicalOpinionUnlockPayload,
  AISupportStartPayload,
  AISupportStartResponse,
  AISupportMessagePayload,
  AISupportFeedbackPayload,
  AISupportSessionDetail,
  AISupportSessionsResponse,
  AISupportKBEntry,
  AISupportKBResponse,
  AISupportKBRevision,
  AISupportDefect,
  AISupportDerived,
  AISupportSessionSummary,
  AISupportAction,
  MedicalOpinionPdfResponse,
  AddMedicalOpinionFilePayload,
  AddMedicalOpinionFileResponse,
  DeleteMedicalOpinionFilePayload,
} from "../types/api";

const API_URL = process.env.REACT_APP_API_URL || "https://dev-backend.taliaz-cm.com";

/**
 * Record failed API calls so the AI Support assistant can read them instead of
 * asking the therapist to reproduce the problem.
 *
 * Installed on the default axios instance, because most methods in this file
 * call `axios.get`/`axios.post` with a full URL — AND on `this.client`, which
 * does not inherit them: an instance from `axios.create()` carries its own
 * interceptor chain. The three calls that go through `this.client` are
 * `oauth_login`, `google_login_init` and `google_login`, so until both were
 * covered the sign-in path — the one place a user cannot work around a failure
 * — was the one path leaving no diagnostic trace.
 *
 * Metadata only — never request or response bodies: a request id is enough to
 * find the whole story in the server logs, and bodies would blow the envelope's
 * size budget. Login bodies carry credentials, which is a second reason.
 */
const instrumentedInstances = new WeakSet<object>();
let defaultInstanceInstrumented = false;

function installFailedRequestInterceptor(instance?: AxiosInstance): void {
  const target = instance ?? axios;
  if (instance) {
    if (instrumentedInstances.has(instance)) return;
    instrumentedInstances.add(instance);
  } else {
    if (defaultInstanceInstrumented) return;
    defaultInstanceInstrumented = true;
  }
  target.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const config = error?.config || {};
        const response = error?.response;
        const headers = response?.headers || {};
        recordFailedRequest({
          method: (config.method || "get").toUpperCase(),
          // `this.client` calls pass a path and resolve it against baseURL, so
          // record the resolved URL rather than a bare "/google_login_init"
          // that does not say which backend it went to.
          url: (config.baseURL || "") + (config.url || ""),
          status: response?.status,
          request_id: headers["x-request-id"] || headers["request-id"],
          detail:
            typeof response?.data?.detail === "string"
              ? response.data.detail
              : error?.message,
        });
      } catch {
        /* diagnostics must never change how a failure propagates */
      }
      return Promise.reject(error);
    }
  );
}

/**
 * Statuses worth trying again. A 4xx is the server saying no and will say no
 * again; these are the server not answering, which is the shape a datastore
 * stall or a load-balancer cut takes and the shape that clears on its own.
 */
const TRANSIENT_STATUSES = new Set([429, 502, 503, 504]);

function isTransient(error: any): boolean {
  const status = error?.response?.status;
  if (status === undefined) {
    // No response at all: a dropped connection, DNS, or our own timeout.
    return true;
  }
  return TRANSIENT_STATUSES.has(status);
}

/**
 * Retry an **idempotent** request through a transient failure, under a total
 * time budget.
 *
 * Only ever wrap calls that can be repeated with no side effect. Signing in is
 * not one of them: an OAuth code is single-use, and repeating a password
 * attempt can count against the lockout. Building an authorization URL is.
 *
 * The budget is the point, not the attempt count. A fast failure — a 503 that
 * returns at once — retries immediately and costs the user nothing, which is
 * the case retrying is for. A stall burns the per-request timeout on every
 * attempt, and three of those in a row is a user watching a button for the best
 * part of a minute, which is worse than an honest error at fifteen seconds. So
 * a retry only happens if there is budget left to finish it in.
 */
async function retryIdempotent<T>(
  label: string,
  attempt: () => Promise<T>,
  options: { delaysMs?: number[]; budgetMs?: number; attemptMs?: number } = {}
): Promise<T> {
  const delaysMs = options.delaysMs ?? [400, 1200];
  const budgetMs = options.budgetMs ?? 20000;
  const attemptMs = options.attemptMs ?? 0;
  const startedAt = Date.now();
  let lastError: any;

  for (let i = 0; i <= delaysMs.length; i++) {
    try {
      return await attempt();
    } catch (error: any) {
      lastError = error;
      if (i === delaysMs.length || !isTransient(error)) break;
      const elapsed = Date.now() - startedAt;
      const wouldFinishAt = elapsed + delaysMs[i] + attemptMs;
      if (wouldFinishAt > budgetMs) {
        console.warn(
          `[API] ${label} failed after ${elapsed}ms; not retrying, ` +
            `another attempt would exceed the ${budgetMs}ms budget`
        );
        break;
      }
      console.warn(
        `[API] ${label} failed (${error?.response?.status ?? error?.code ?? "no response"}) ` +
          `after ${elapsed}ms, retrying in ${delaysMs[i]}ms — ` +
          `attempt ${i + 2} of ${delaysMs.length + 1}`
      );
      await new Promise((resolve) => setTimeout(resolve, delaysMs[i]));
    }
  }
  throw lastError;
}

class ApiService {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = API_URL;
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });
    installFailedRequestInterceptor();
    installFailedRequestInterceptor(this.client);
  }

  // Helper to map API response to User interface
  private mapUserResponse(apiResponse: OAuthLoginApiResponse | AuthMeApiResponse): User {
    const baseUser = {
      id: apiResponse.user_id || (apiResponse as AuthMeApiResponse).id,
      name: (apiResponse as AuthMeApiResponse).name || "",
      email: (apiResponse as AuthMeApiResponse).email || "",
      type: apiResponse.type,
      role: apiResponse.role || apiResponse.type || "",
      institution_id: apiResponse.institution_id,
    };

    if ("active" in apiResponse) {
      return {
        ...baseUser,
        active: apiResponse.active,
        clinic_admin: apiResponse.clinic_admin,
        sub_clinic: apiResponse.sub_clinic,
        sub_clinic_list: apiResponse.sub_clinic_list,
        clinicservices_id: apiResponse.clinicservices_id,
        show_approve_treatment: apiResponse.show_approve_treatment,
        show_prep_meeting: apiResponse.show_prep_meeting,
        show_clicks_tasks: apiResponse.show_clicks_tasks,
        has_alerts: apiResponse.has_alerts,
        new_alerts: apiResponse.new_alerts,
        phone_ext: apiResponse.phone_ext,
        manage_users: apiResponse.manage_users,
        manage_clalit_excel: apiResponse.manage_clalit_excel,
        whatsapp: apiResponse.whatsapp,
        mobile_tracking: apiResponse.mobile_tracking,
        prompt_developer: apiResponse.prompt_developer,
        // Was declared on User but never copied here, so it always arrived
        // undefined and the Clicks prescription button never hid.
        hide_update_prescription: apiResponse.hide_update_prescription,
        ai_support: apiResponse.ai_support,
        ai_support_admin: apiResponse.ai_support_admin,
      };
    }

    return baseUser;
  }

  // Helper to create client with authorization header
  private getAuthClient(token: string): AxiosInstance {
    return axios.create({
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Authentication
  async login(data: OAuthLoginPayload): Promise<LoginResponse> {
    console.log("[API] Login request to /oauth_login", {
      email: data.email,
      skip_2fa: data.skip_2fa,
      twofa_code: data.twofa_code ? "***" : undefined
    });
    try {
      const response = await this.client.post<OAuthLoginApiResponse>("/oauth_login", data);
      console.log("[API] Login successful, response structure:", response.data);
      const apiResponse = response.data;
      const user = this.mapUserResponse(apiResponse);
      const loginResponse: LoginResponse = {
        authToken: apiResponse.authToken || apiResponse.token || "",
        user,
      };
      console.log("[API] Mapped login response:", loginResponse);
      return loginResponse;
    } catch (error: any) {
      console.error("[API] Login failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * Ask the backend for the Google authorization URL.
   *
   * Retried, because it is a pure read that builds a URL — repeating it costs
   * nothing and changes nothing — and because it sits in front of sign-in,
   * where a single blip locks somebody out of the product entirely. A one-off
   * stall on 2026-09-14 did exactly that and left the user with "Google login
   * initialization failed. Please try again." and no way to tell whether trying
   * again would help.
   *
   * The per-attempt timeout sits above the backend's own 6s deadline on this
   * call, so when Xano stalls the server's 504 wins the race and the user gets
   * a real reason rather than a bare network error.
   *
   * The numbers are chosen so a STALL still gets a second attempt: 7s + 0.4s +
   * 7s fits inside the 18s budget, and a third would not. Tightening the budget
   * to 15s looks kinder and is not -- it makes the arithmetic refuse the retry
   * outright, so the one failure shape this exists for would get a single try.
   * A fast failure is unaffected and still gets all three attempts, inside two
   * seconds.
   */
  async googleLoginInit(redirectUri: string): Promise<{ authUrl: string }> {
    const ATTEMPT_MS = 7000;
    return retryIdempotent("google_login_init", async () => {
      const response = await this.client.get<{ authUrl: string }>("/google_login_init", {
        params: { redirect_uri: redirectUri },
        timeout: ATTEMPT_MS,
      });
      if (!response.data?.authUrl) {
        // A 200 with no URL is a server fault, not a user one. Shaped like an
        // axios error so the retry and the caller both read it the same way.
        const error: any = new Error("google_login_init returned no authUrl");
        error.response = { status: 502, data: { detail: "Google sign-in did not return an authorization URL" } };
        throw error;
      }
      return response.data;
    }, { delaysMs: [400, 1200], budgetMs: 18000, attemptMs: ATTEMPT_MS });
  }

  async googleLogin(
    code: string,
    redirectUri: string,
    options?: {
      twofa_code?: string;
      google_secret?: string;
      skip_2fa?: boolean;
    }
  ): Promise<LoginResponse> {
    const isInitialCallback = !options?.twofa_code && !options?.skip_2fa;

    console.log("[API] Google login with", isInitialCallback ? "initial callback" : "2FA data");

    const payload: any = {
      is_google_code: isInitialCallback,
      redirect_uri: redirectUri,
    };

    if (isInitialCallback) {
      // Initial callback from Google
      payload.google_code = code;
    } else {
      // 2FA flow
      if (options?.twofa_code) {
        payload.twofa_code = options.twofa_code;
      }
      if (options?.google_secret) {
        payload.google_secret = options.google_secret;
      }
      if (options?.skip_2fa !== undefined) {
        payload.skip_2fa = options.skip_2fa;
      }
    }

    const response = await this.client.post<OAuthLoginApiResponse>("/google_login", payload);
    console.log("[API] Google login successful");
    const apiResponse = response.data;
    const user = this.mapUserResponse(apiResponse);
    return {
      authToken: apiResponse.authToken || apiResponse.token || "",
      user,
    };
  }



  // Schedule-page (IL yoman) link for a patient; the token embeds the current
  // user's id so meetings booked through it record the user's name as src.
  async getPatientScheduleLink(
    token: string,
    patientId: number
  ): Promise<{ link: string }> {
    console.log("[API] Get patient schedule link request");
    const response = await axios.get<{ link: string }>(
      `${this.baseURL}/patient_schedule_link?token=${token}&patient_id=${patientId}`
    );
    return response.data;
  }

  async getAuthMe(token: string): Promise<User> {
    console.log("[API] Get auth me request");
    const response = await axios.get<AuthMeApiResponse>(
      `${this.baseURL}/auth_me?token=${token}`
    );
    console.log("[API] Auth me successful");
    const user = this.mapUserResponse(response.data);
    return user;
  }

  // Patients - Updated to POST
  async getPatientsIntakePaging(
    data: GetPatientsIntakePayload
  ): Promise<GetPatientsIntakeResponse> {
    console.log("[API] Get patients intake paging request");

    const response = await axios.post<GetPatientsIntakeResponse>(
      `${this.baseURL}/patients_intake_paging`,
      {
        token: data.token,
        clinic_id: data.clinic_id,
        offset: data.offset,
        pageSize: data.pageSize,
        search: data.search,
        status: data.status,
        user_id: data.user_id,
        sub_clinic: data.sub_clinic || "",
        source: data.source || "",
        sortedBy: data.sortedBy || [],
      }
    );

    console.log("[API] Patients intake paging successful");
    return response.data;
  }

  // Top-bar patient search. Each search_by value hits its own Xano search endpoint
  // behind the backend (patients_search_name / _tz / _phone / _hm_id) and comes back
  // enriched like a patients-table row, so the results reuse the same row rendering
  // and context menu.
  async searchPatients(
    payload: SearchPatientsPayload
  ): Promise<SearchPatientsResponse> {
    console.log("[API] Search patients request by", payload.search_by);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        search_by: payload.search_by,
        value: payload.value,
      });

      const response = await axios.get<SearchPatientsResponse>(
        `${this.baseURL}/patients_search?${params.toString()}`
      );
      console.log(`[API] Patient search returned ${response.data?.total_count ?? 0} results`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Search patients failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Failed to search patients"
      );
    }
  }

  // Tasks
  async getPatientsTasks(
    payload: GetPatientsTasksPayload
  ): Promise<GetPatientsTasksResponse> {
    console.log("[API] Get patients tasks request");
    const params = new URLSearchParams({
      token: payload.token,
      clinic_id: payload.clinic_id.toString(),
    });

    if (payload.user_id !== undefined) {
      params.append("user_id", payload.user_id.toString());
    }

    const response = await axios.get<GetPatientsTasksResponse>(
      `${this.baseURL}/patients_tasks?${params.toString()}`
    );
    console.log(`[API] Retrieved ${response.data.length} tasks`);
    return response.data;
  }

  // Clinics
  async getClinics(token: string): Promise<GetClinicsResponse> {
    console.log("[API] Get clinics request");
    const response = await axios.get<ClinicApiResponse[]>(
      `${this.baseURL}/clinics?token=${token}&triage=false`
    );
    console.log("[API] Clinics successful");
    return {
      items: response.data.map((clinic) => ({
        id: clinic.id,
        name: clinic.name,
        active: clinic.active,
        institution_id: clinic.institution_id,
        clinic_admin: clinic.clinic_admin,
        sub_clinic: clinic.sub_clinic,
        show_approve_treatment: clinic.show_approve_treatment,
        show_prep_meeting: clinic.show_prep_meeting,
        show_clicks_tasks: clinic.show_clicks_tasks,
      })),
    };
  }

  // 2FA
  async setup2FA(email: string): Promise<Setup2FAResponse> {
    console.log("[API] Setup 2FA request for email:", email);
    try {
      const response = await axios.post<Setup2FAResponse>(
        `${this.baseURL}/setup_2fa`,
        { email }
      );

      console.log("[API] 2FA setup successful");
      return response.data;
    } catch (error: any) {
      console.error("[API] Setup 2FA failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Forgot Password
  async sendForgotPassword(email: string): Promise<{ message: string }> {
    console.log("[API] Send forgot password request for email:", email);
    try {
      const response = await axios.post<{ message: string }>(
        `${this.baseURL}/send_forgot_password`,
        { email }
      );
      console.log("[API] Forgot password email sent successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Send forgot password failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Change Password
  async changePassword(payload: ChangePasswordPayload): Promise<ChangePasswordResponse> {
    console.log("[API] Change password request for email:", payload.email);
    try {
      const response = await axios.post<ChangePasswordResponse>(
        `${this.baseURL}/auth_change_password`,
        payload
      );
      console.log("[API] Password change response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("[API] Change password failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Check Forgot Password Key
  async checkForgotPasswordKey(payload: CheckForgotPasswordKeyPayload): Promise<CheckForgotPasswordKeyResponse> {
    console.log("[API] Check forgot password key request");
    try {
      const response = await axios.get<CheckForgotPasswordKeyResponse>(
        `${this.baseURL}/check_forgot_password_key`,
        { params: payload }
      );
      console.log("[API] Check forgot password key response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("[API] Check forgot password key failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Update Forgot Password
  async updateForgotPassword(payload: UpdateForgotPasswordPayload): Promise<UpdateForgotPasswordResponse> {
    console.log("[API] Update forgot password request", payload);
    try {
      const response = await axios.post<UpdateForgotPasswordResponse>(
        `${this.baseURL}/update_forgot_password`,
        {
          key: payload.key,
          password: payload.newPassword
        }
      );
      console.log("[API] Update forgot password response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("[API] Update forgot password failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Logout
  async logout(): Promise<void> {
    console.log("[API] Logout request");
    // In this implementation, logout is client-side only
    // If backend has a logout endpoint, call it here
  }

  // Meetings
  async getMeetingsByDates(
    payload: GetMeetingsByDatesPayload
  ): Promise<MeetingsByDatesResponse> {
    console.log("[API] Get meetings by dates request:", payload);
    const params = new URLSearchParams({
      token: payload.token,
      meeting_type_id: payload.meeting_type_id.toString(),
      clinic_id: payload.clinic_id.toString(),
      user_id: payload.user_id.toString(),
      only_my: payload.only_my.toString(),
      from_date: payload.from_date.toString(),
      to_date: payload.to_date.toString(),
    });
    // Each selected status is sent as its own `status=` param; omitted entirely
    // when no statuses are selected (i.e. "All Statuses").
    (payload.status ?? []).forEach((s) => params.append("status", s));

    const response = await axios.get<MeetingsByDatesResponse>(
      `${this.baseURL}/meetings_by_dates?${params.toString()}`
    );
    console.log("[API] Meetings response:", response.data);
    return response.data;
  }

  async getAvailableSlots(
    token: string,
    user_id: number,
    from_date: number,
    to_date: number
  ): Promise<Array<{ start: string; end: string }>> {
    console.log("[API] Get available slots request:", { token, user_id, from_date, to_date });
    const params = new URLSearchParams({
      token,
      user_id: user_id.toString(),
      from_date: new Date(from_date).toISOString().split('T')[0],
      to_date: new Date(to_date).toISOString().split('T')[0],
    });

    const response = await axios.get<Array<{ start: string; end: string }>>(
      `${this.baseURL}/get_availbale_slots?${params.toString()}`
    );
    console.log("[API] Available slots response:", response.data);
    return response.data;
  }

  async updateMeetingStatus(
    payload: UpdateMeetingStatusPayload
  ): Promise<UpdateMeetingStatusResponse> {
    console.log("[API] Update meeting status request:", payload);
    try {
      const response = await axios.post<UpdateMeetingStatusResponse>(
        `${this.baseURL}/meetingUpdateStatus`,
        payload
      );
      console.log("[API] Meeting status updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update meeting status failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updateMeetingPrepStatus(
    token: string,
    meetingId: number,
    prepStatus: string,
    meetingtypeId: number,
    clinicId: number,
    onlyMy: boolean
  ): Promise<any[]> {
    console.log("[API] Update meeting prep status request:", { meetingId, prepStatus });
    try {
      const response = await axios.post<any[]>(
        `${this.baseURL}/update_meeting_prep_status`,
        {
          token,
          meeting_id: meetingId,
          prep_status: prepStatus,
          source: "meetings",
          meetingtype_id: meetingtypeId,
          period: "all",
          clinic_id: clinicId,
          only_my: onlyMy
        }
      );
      console.log("[API] Meeting prep status updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update meeting prep status failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Get clinic users
  async getUsers(payload: GetUsersPayload): Promise<GetUsersResponse> {
    console.log("[API] Get users request:", payload);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        user_type: payload.user_type,
        institution_id: payload.institution_id.toString(),
      });

      const response = await axios.get<GetUsersResponse>(
        `${this.baseURL}/users?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} users`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get users failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Create clinic user
  async createUser(payload: CreateUserPayload): Promise<CreateUserResponse> {
    console.log("[API] Create user request:", payload.email);
    try {
      const response = await axios.post<CreateUserResponse>(
        `${this.baseURL}/user`,
        payload
      );
      console.log("[API] User created successfully:", response.data.id);
      return response.data;
    } catch (error: any) {
      console.error("[API] Create user failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Update clinic user
  async updateUser(payload: UpdateUserPayload): Promise<UpdateUserResponse> {
    console.log("[API] Update user request:", payload.id);
    try {
      const response = await axios.patch<UpdateUserResponse>(
        `${this.baseURL}/user`,
        payload
      );
      console.log("[API] User updated successfully:", response.data.id);
      return response.data;
    } catch (error: any) {
      console.error("[API] Update user failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Activate/Deactivate user
  async activateUser(token: string, id: number, active: boolean): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Activate user request:", { id, active });
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/activate_user`,
        {
          token,
          id,
          active,
        }
      );
      console.log("[API] User activation status updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Activate user failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }


  // Meeting Types
  async getMeetingTypes(token: string, forFilter: boolean = true): Promise<GetMeetingTypesResponse> {
    console.log("[API] Get meeting types request");
    try {
      const response = await axios.get<GetMeetingTypesResponse>(
        `${this.baseURL}/meeting_types?token=${token}&for_filter=${forFilter}`
      );
      console.log(`[API] Retrieved ${response.data.length} meeting types`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get meeting types failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // List Views
  async createListView(payload: CreateListViewPayload): Promise<ListView> {
    console.log("[API] Create list view request:", payload.name);
    try {
      const response = await axios.post<ListView>(
        `${this.baseURL}/list_view`,
        payload
      );
      console.log("[API] List view created successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Create list view failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getListViewsByOwner(
    ownerId: number,
    listName: string,
    token: string
  ): Promise<GetListViewsResponse> {
    console.log("[API] Get list views request for owner:", ownerId, "list:", listName);
    try {
      const response = await axios.get<GetListViewsResponse>(
        `${this.baseURL}/list_view/by_owner/${ownerId}/${listName}?token=${token}`
      );
      console.log(`[API] Retrieved ${response.data.length} list views`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get list views failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updateListView(payload: UpdateListViewPayload): Promise<ListView> {
    console.log("[API] Update list view request:", payload.id);
    try {
      const { id, ...updateData } = payload;
      const response = await axios.patch<ListView>(
        `${this.baseURL}/list_view/${id}`,
        updateData
      );
      console.log("[API] List view updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update list view failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updatePatientComments(
    token: string,
    patientId: number,
    newComments: string
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Update patient comments request for patient:", patientId);
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/updatePatientComments`,
        {
          token,
          patient_id: patientId.toString(),
          new_comments: newComments,
        }
      );
      console.log("[API] Patient comments updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update patient comments failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updatePatientInfo(
    token: string,
    patientId: number | string,
    updatedData: any
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Update patient info request for patient:", patientId);
    try {
      // Include the id inside the patient object as part of the array
      const patientWithId = {
        id: patientId,
        ...updatedData,
      };

      const response = await axios.patch<{ success: boolean; message?: string }>(
        `${this.baseURL}/patient`,
        {
          token,
          input: [patientWithId], // Send as array with one item
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const responseData = Array.isArray(response.data) ? (response.data as any)[0] : response.data as any;
      console.log("[API] Patient info response data:", responseData);
      if (responseData?.output_message === 'ERROR') {
        console.log(responseData.message)
        throw new Error(responseData.message || 'Failed to update patient information');
      }
      console.log("[API] Patient info updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update patient info failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getPatientMeetings(
    token: string,
    patientId: string | number
  ): Promise<GetPatientMeetingsResponse> {
    console.log("[API] Get patient meetings request for patient:", patientId);
    try {
      const params = new URLSearchParams({
        token,
        patient_id: patientId.toString(),
      });

      const response = await axios.get<GetPatientMeetingsResponse>(
        `${this.baseURL}/patient_page_meetings?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} meetings for patient ${patientId}`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get patient meetings failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getAdhocPatients(payload: {
    token: string;
    user_id: number;
    days_from_last_cm_followup: number;
    days_to_next_cm_followup: number;
  }): Promise<any[]> {
    console.log("[API] Get adhoc patients request:", payload);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        user_id: payload.user_id.toString(),
        days_from_last_cm_followup: payload.days_from_last_cm_followup.toString(),
        days_to_next_cm_followup: payload.days_to_next_cm_followup.toString(),
      });

      const response = await axios.get<any>(
        `${this.baseURL}/adhoc_patients?${params.toString()}`
      );
      // Backend proxies Xano; normalize either a bare list or a wrapped one.
      const data = response.data;
      const items = Array.isArray(data) ? data : data?.patients || data?.items || [];
      console.log(`[API] Retrieved ${items.length} adhoc patients`);
      return items;
    } catch (error: any) {
      console.error("[API] Get adhoc patients failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async createAdHocMeeting(payload: {
    token: string;
    patient_id: number;
    meetingtype_id: number;
    start: number; // epoch ms
    length: number; // minutes
  }): Promise<{ success?: boolean; meeting_id?: number }> {
    console.log("[API] Create ad-hoc meeting for patient:", payload.patient_id);
    try {
      const response = await axios.post<{ success?: boolean; meeting_id?: number }>(
        `${this.baseURL}/createAdHocMeeting`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      console.log("[API] Ad-hoc meeting created:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("[API] Create ad-hoc meeting failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw new Error(error.response?.data?.message || error.message || "Failed to create meeting");
    }
  }

  // Get Meetings for Patient (for task dropdown)
  async getMeetingsForPatient(
    token: string,
    patientId: string | number
  ): Promise<PatientMeeting[]> {
    console.log("[API] Get meetings for patient request for patient:", patientId);
    try {
      const params = new URLSearchParams({
        token,
        patient_id: patientId.toString(),
      });

      const response = await axios.get<PatientMeeting[]>(
        `${this.baseURL}/meetings_for_patient?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} meetings for patient ${patientId}`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get meetings for patient failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }


  // Get Patient Meeting Summaries
  async getPatientMeetingSummaries(
    token: string,
    patientId: string | number
  ): Promise<Array<{ id: number; name: string; summary: string }>> {
    console.log("[API] Get patient meeting summaries request for patient:", patientId);
    try {
      const params = new URLSearchParams({
        token,
        patient_id: patientId.toString(),
      });

      const response = await axios.get<Array<{ id: number; name: string; summary: string }>>(
        `${this.baseURL}/get_patient_meeting_summaries?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} summaries for patient ${patientId}`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get patient meeting summaries failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async deleteListView(id: number, token: string): Promise<void> {
    console.log("[API] Delete list view request:", id);
    try {
      await axios.delete(
        `${this.baseURL}/list_view/${id}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            id,
            token,
          },
        }
      );
      console.log("[API] List view deleted successfully");
    } catch (error: any) {
      console.error("[API] Delete list view failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async makeCall(
    token: string,
    patientId: string | number,
    phone: string
  ): Promise<{ status: string; message?: string }> {
    console.log("[API] Make call request for patient:", patientId);
    try {
      const response = await axios.post<{ status: string; message?: string }>(
        `${this.baseURL}/call`,
        {
          token,
          patient_id: patientId.toString(),
          phone,
        }
      );
      console.log("[API] Call response:", response.data);

      // Check if the API returned an error status in the response body
      if (response.data.status === "Error") {
        const errorMessage = response.data.message || "Call failed";
        console.error("[API] Call failed with error status:", errorMessage);
        throw new Error(errorMessage);
      }

      return response.data;
    } catch (error: any) {
      console.error("[API] Make call failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });

      // If the error is already a custom error we threw, re-throw it
      if (error.message && !error.response) {
        throw error;
      }

      // Check if API returned an error in the response body
      if (error.response?.data?.status === "Error") {
        throw new Error(error.response.data.message || "Call failed");
      }

      throw error;
    }
  }

  async updateEmail(
    token: string,
    patientId: string | number,
    newEmail: string
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Update email request for patient:", patientId);
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/updateemail`,
        {
          token,
          patient_id: patientId.toString(),
          new_email: newEmail,
        }
      );
      console.log("[API] Email updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update email failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Meeting Summary
  async getMeetingSummary(
    payload: GetMeetingSummaryPayload
  ): Promise<MeetingSummary> {
    console.log("[API] Get meeting summary request for meeting:", payload.meeting_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        meeting_id: payload.meeting_id.toString(),
      });

      const response = await axios.get<MeetingSummary>(
        `${this.baseURL}/meeting_summary?${params.toString()}`,
        { timeout: 30000 }
      );
      console.log("[API] Meeting summary retrieved successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Get meeting summary failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updateMeetingSummary(
    payload: UpdateMeetingSummaryPayload
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Update meeting summary request for meeting:", payload.meeting_id);
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/meeting_summary`,
        payload,
        { timeout: 30000 }
      );
      console.log("[API] Meeting summary updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update meeting summary failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async sendSummaryPDF(
    payload: UpdateMeetingSummaryPayload
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Send summary PDF request for meeting:", payload.meeting_id);
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/meeting_summary`,
        {
          ...payload,
          send: "true",
        },
        { timeout: 30000 }
      );
      console.log("[API] Summary PDF sent successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Send summary PDF failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async approvePsySummary(
    payload: UpdateMeetingSummaryPayload
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Approve PSY summary request for meeting:", payload.meeting_id);
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/meeting_summary`,
        {
          ...payload,
          send: "false",
          approved: "true",
        },
        { timeout: 30000 }
      );
      console.log("[API] PSY summary approved successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Approve PSY summary failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async addPrescriptionToClicks(
    payload: UpdateMeetingSummaryPayload
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Add prescription to Clicks request for meeting:", payload.meeting_id);
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/meeting_summary`,
        {
          ...payload,
          update_prescription: "true",
        },
        { timeout: 30000 }
      );
      console.log("[API] Prescription added to Clicks successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Add prescription to Clicks failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getMeetingSummaryPDF(
    payload: GetMeetingSummaryPDFPayload
  ): Promise<PDFResponse> {
    console.log("[API] Get meeting summary PDF request for meeting:", payload.meeting_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        meeting_id: payload.meeting_id.toString(),
      });

      const response = await axios.get<PDFResponse>(
        `${this.baseURL}/meeting_summary_pdf?${params.toString()}`,
        {
          timeout: 30000,
        }
      );
      console.log("[API] Meeting summary PDF retrieved successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Get meeting summary PDF failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Diagnosis
  async getDiagnosis(payload: GetDiagnosisPayload): Promise<GetDiagnosisResponse> {
    console.log("[API] Get diagnosis codes request");
    try {
      const params = new URLSearchParams({
        token: payload.token,
      });

      const response = await axios.get<GetDiagnosisResponse>(
        `${this.baseURL}/diagnosis?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} diagnosis codes`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get diagnosis codes failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Medication Types
  async getMedicationTypes(payload: GetMedicationTypesPayload): Promise<GetMedicationTypesResponse> {
    console.log("[API] Get medication types request");
    try {
      const params = new URLSearchParams({
        token: payload.token,
      });

      const response = await axios.get<GetMedicationTypesResponse>(
        `${this.baseURL}/medication_type?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} medication types`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get medication types failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // CM Meeting Summary
  async getCMMeetingSummary(
    payload: GetCMMeetingSummaryPayload
  ): Promise<CMMeetingSummary> {
    console.log("[API] Get CM meeting summary request for meeting:", payload.meeting_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        meeting_id: payload.meeting_id.toString(),
      });

      const response = await axios.get<CMMeetingSummary>(
        `${this.baseURL}/cm_meeting_summary?${params.toString()}`,
        { timeout: 30000 }
      );
      console.log("[API] CM meeting summary retrieved successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Get CM meeting summary failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updateCMMeetingSummary(
    payload: UpdateCMMeetingSummaryPayload
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Update CM meeting summary request for meeting:", payload.meeting_id);
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/cm_meeting_summary`,
        payload,
        { timeout: 30000 }
      );
      console.log("[API] CM meeting summary updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update CM meeting summary failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async approveCMSummary(
    payload: UpdateCMMeetingSummaryPayload
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Approve CM summary request for meeting:", payload.meeting_id);
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/cm_meeting_summary`,
        {
          ...payload,
          approved: "true",
        },
        { timeout: 30000 }
      );
      console.log("[API] CM summary approved successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Approve CM summary failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updateCMMeeting(
    payload: UpdateCMMeetingPayload
  ): Promise<UpdateCMMeetingResponse> {
    console.log("[API] Update CM meeting request for meeting:", payload.id);
    try {
      const response = await axios.post<UpdateCMMeetingResponse>(
        `${this.baseURL}/cm_meeting`,
        payload,
        { timeout: 30000 }
      );
      console.log("[API] CM meeting response:", response.data.output_message);
      return response.data;
    } catch (error: any) {
      console.error("[API] Update CM meeting failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // PSY Meeting Insights
  async getPSYMeetingInsights(
    payload: GetPSYMeetingInsightsPayload
  ): Promise<PSYMeetingInsights> {
    console.log("[API] Get PSY meeting insights request for meeting:", payload.meeting_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        meeting_id: payload.meeting_id.toString(),
      });

      const response = await axios.get<PSYMeetingInsights>(
        `${this.baseURL}/get_insights?${params.toString()}`,
        { timeout: 30000 }
      );
      console.log("[API] PSY meeting insights retrieved successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Get PSY meeting insights failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updatePSYMeeting(
    payload: UpdatePSYMeetingPayload
  ): Promise<UpdatePSYMeetingResponse> {
    console.log("[API] Update PSY meeting request for meeting:", payload.meeting_id);
    try {
      // TODO: Update endpoint URL when backend confirms
      const response = await axios.post<UpdatePSYMeetingResponse>(
        `${this.baseURL}/update_psy_meeting`,
        payload,
        { timeout: 30000 }
      );
      console.log("[API] PSY meeting response:", response.data.output_message);
      return response.data;
    } catch (error: any) {
      console.error("[API] Update PSY meeting failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getPSYMeetingAgenda(
    payload: GetPSYMeetingAgendaPayload
  ): Promise<PSYMeetingAgenda> {
    console.log("[API] Get PSY meeting agenda request for meeting:", payload.meeting_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        meeting_id: payload.meeting_id.toString(),
      });

      const response = await axios.get<string>(
        `${this.baseURL}/get_agenda?${params.toString()}`,
        {
          timeout: 30000,
          responseType: 'text'
        }
      );
      console.log("[API] PSY meeting agenda retrieved successfully");
      // Return the plain text response wrapped in the expected interface
      return { message: response.data };
    } catch (error: any) {
      console.error("[API] Get PSY meeting agenda failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async savePSYMeeting(
    payload: SavePSYMeetingPayload
  ): Promise<SavePSYMeetingResponse> {
    console.log("[API] Save PSY meeting request for meeting:", payload.id);
    try {
      const response = await axios.post<SavePSYMeetingResponse>(
        `${this.baseURL}/psy_meeting`,
        payload,
        { timeout: 30000 }
      );
      console.log("[API] PSY meeting saved successfully:", response.data.output_message);
      return response.data;
    } catch (error: any) {
      console.error("[API] Save PSY meeting failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async generateSummary(payload: {
    token: string;
    meeting_id: number;
  }): Promise<{ output_message: string }> {
    console.log("[API] Generate summary request for meeting:", payload.meeting_id);
    try {
      const response = await axios.post<{ output_message: string }>(
        `${this.baseURL}/generate_summary`,
        payload,
        { timeout: 30000 }
      );
      console.log("[API] Generate summary response:", response.data.output_message);
      return response.data;
    } catch (error: any) {
      console.error("[API] Generate summary failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async checkIfSummaryReady(payload: {
    token: string;
    meeting_id: number;
  }): Promise<boolean> {
    console.log("[API] Check if summary ready for meeting:", payload.meeting_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        meeting_id: payload.meeting_id.toString(),
      });

      const response = await axios.get<boolean>(
        `${this.baseURL}/check_if_summary_ready?${params.toString()}`,
        { timeout: 10000 }
      );
      console.log("[API] Summary ready status:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("[API] Check if summary ready failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async finishMeeting(
    payload: FinishMeetingPayload
  ): Promise<FinishMeetingResponse> {
    console.log("[API] Finish meeting request for meeting:", payload.meeting_id);
    try {
      const response = await axios.post<FinishMeetingResponse>(
        `${this.baseURL}/finish_meeting`,
        payload,
        { timeout: 30000 }
      );
      console.log("[API] Meeting finish response:", response.data.status);
      return response.data;
    } catch (error: any) {
      console.error("[API] Finish meeting failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getPatientPage(
    payload: GetPatientPagePayload
  ): Promise<GetPatientPageResponse> {
    console.log("[API] Get patient page request for patient:", payload.patient_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        patient_id: payload.patient_id.toString(),
      });

      const response = await axios.get<GetPatientPageResponse>(
        `${this.baseURL}/patient_page?${params.toString()}`
      );
      console.log("[API] Patient page retrieved successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Get patient page failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getPatientFiles(
    payload: GetPatientFilesPayload
  ): Promise<GetPatientFilesResponse> {
    console.log("[API] Get patient files request for patient:", payload.patient_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        patient_id: payload.patient_id.toString(),
      });

      const response = await axios.get<GetPatientFilesResponse>(
        `${this.baseURL}/patient_files?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} files for patient ${payload.patient_id}`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get patient files failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getPatientFileTypes(
    payload: GetPatientFileTypesPayload
  ): Promise<GetPatientFileTypesResponse> {
    console.log("[API] Get patient file types request");
    try {
      const params = new URLSearchParams({
        token: payload.token,
        owner: payload.owner,
      });

      const response = await axios.get<GetPatientFileTypesResponse>(
        `${this.baseURL}/patient_file_type?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} file types`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get patient file types failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updatePatientFile(
    payload: UpdatePatientFilePayload
  ): Promise<UpdatePatientFileResponse> {
    console.log("[API] Update patient file request:", payload.file_id);
    try {
      const response = await axios.patch<UpdatePatientFileResponse>(
        `${this.baseURL}/patient_file`,
        payload
      );
      console.log("[API] Patient file updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update patient file failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async downloadFile(fileId: string, filename: string): Promise<void> {
    console.log("[API] Download file request:", fileId);
    try {
      const params = new URLSearchParams({
        id: fileId,
        filename: filename,
      });

      const response = await axios.get(
        `${this.baseURL}/download?${params.toString()}`,
        {
          responseType: 'blob',
        }
      );

      // Create a blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("[API] File downloaded successfully");
    } catch (error: any) {
      console.error("[API] Download file failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async uploadPatientFile(
    payload: PatientUploadFilePayload
  ): Promise<PatientUploadFileResponse> {
    console.log("[API] Upload patient file request for patient:", payload.patient_id);
    try {
      const response = await axios.post<PatientUploadFileResponse>(
        `${this.baseURL}/patient_upload_file`,
        payload
      );
      console.log("[API] Patient file uploaded successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Upload patient file failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getUploadFileTypes(
    payload: GetUploadFileTypesPayload
  ): Promise<GetUploadFileTypesResponse> {
    console.log("[API] Get upload file types, lang:", payload.lang);
    try {
      const params = new URLSearchParams({ token: payload.token, lang: payload.lang });
      const response = await axios.get<GetUploadFileTypesResponse>(
        `${this.baseURL}/upload_file_types?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} upload file types`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get upload file types failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async uploadFilesRequest(
    payload: UploadFilesRequestPayload
  ): Promise<UploadFilesRequestResponse> {
    console.log("[API] Upload files request for patient:", payload.patient_id, "meeting:", payload.meeting_id);
    console.log("[API] Files content:", payload.files_content);
    try {
      const response = await axios.post<UploadFilesRequestResponse>(
        `${this.baseURL}/upload_files_request`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
          },
        }
      );
      console.log("[API] Upload files request sent successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Upload files request failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }


  async updatePatientSuicidalQuestionnaire(
    payload: UpdatePatientSuicidalQuestionnairePayload
  ): Promise<UpdatePatientSuicidalQuestionnaireResponse> {
    console.log("[API] Update patient suicidal questionnaire for patient:", payload.patient_id);
    try {
      const response = await axios.post<UpdatePatientSuicidalQuestionnaireResponse>(
        `${this.baseURL}/update_patient_suicidal_questionnaire`,
        payload
      );
      console.log("[API] Patient suicidal questionnaire updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update patient suicidal questionnaire failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Update Finish Status
  async updateFinishStatus(
    payload: UpdateFinishStatusPayload
  ): Promise<UpdateFinishStatusResponse> {
    console.log("[API] Update finish status request for patient:", payload.patient_id);
    try {
      const response = await axios.post<UpdateFinishStatusResponse>(
        `${this.baseURL}/update_finish_status`,
        payload
      );
      console.log("[API] Patient finish status updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update finish status failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Update Obligation
  async updateObligation(
    payload: UpdateObligationPayload
  ): Promise<UpdateObligationResponse> {
    console.log("[API] Update obligation request for patient:", payload.patient_id);
    try {
      const response = await axios.post<UpdateObligationResponse>(
        `${this.baseURL}/updateObligation`,
        payload
      );
      console.log("[API] Patient obligation updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update obligation failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Move Patient
  async movePatient(
    payload: MovePatientPayload
  ): Promise<MovePatientResponse> {
    console.log("[API] Move patient request for patient:", payload.patient_id);
    try {
      const response = await axios.post<MovePatientResponse>(
        `${this.baseURL}/move_patient`,
        payload
      );
      console.log("[API] Move patient response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("[API] Move patient failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Get File Image/PDF
  async getFileImage(
    fileId: string
  ): Promise<{ url: string; mimeType: string }> {
    console.log("[API] Get file request for file:", fileId);
    try {
      const response = await axios.get(
        `${this.baseURL}/get_file?id=${fileId}`,
        { responseType: 'blob' }
      );

      const mimeType = response.headers['content-type'] || 'application/octet-stream';
      console.log("[API] File retrieved, mime type:", mimeType);

      const url = URL.createObjectURL(response.data);
      return { url, mimeType };
    } catch (error: any) {
      console.error("[API] Get file failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Patient Questionnaires
  async getPatientQuestionnaires(
    payload: GetPatientQuestionnairesPayload
  ): Promise<GetPatientQuestionnairesResponse> {
    console.log("[API] Get patient questionnaires request for patient:", payload.patient_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        patient_id: payload.patient_id.toString(),
      });

      const response = await axios.get<GetPatientQuestionnairesResponse>(
        `${this.baseURL}/patient_questionnnaires?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} questionnaires for patient ${payload.patient_id}`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get patient questionnaires failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Questionnaire Details
  async getQuestionnaireDetails(
    payload: GetQuestionnaireDetailsPayload
  ): Promise<GetQuestionnaireDetailsResponse> {
    console.log("[API] Get questionnaire details request for questionnaire:", payload.questionnaire_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        questionnaire_id: payload.questionnaire_id.toString(),
      });

      const response = await axios.get<GetQuestionnaireDetailsResponse>(
        `${this.baseURL}/questionnaire_sprw?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.questions?.length || 0} questions for questionnaire ${payload.questionnaire_id}`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get questionnaire details failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Patient Suicidal Questionnaire History
  async getPatientSuicidalQuestHistory(
    payload: GetPatientSuicidalQuestHistoryPayload
  ): Promise<GetPatientSuicidalQuestHistoryResponse> {
    console.log("[API] Get patient suicidal questionnaire history request for patient:", payload.patient_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        patient_id: payload.patient_id.toString(),
      });

      const response = await axios.get<GetPatientSuicidalQuestHistoryResponse>(
        `${this.baseURL}/patient_suicidal_quest_history?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} suicidal questionnaire history items for patient ${payload.patient_id}`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get patient suicidal questionnaire history failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Upload Transcript
  async uploadTranscript(
    payload: UploadTranscriptPayload
  ): Promise<UploadTranscriptResponse> {
    console.log("[API] Upload transcript request for meeting:", payload.meeting_id);
    try {
      const response = await axios.post<UploadTranscriptResponse>(
        `${this.baseURL}/upload_transcript_file`,
        payload
      );
      console.log("[API] Transcript uploaded successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Upload transcript failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Get Meeting Realtime Transcript
  async getMeetingRtTranscript(
    token: string,
    meeting_id: number
  ): Promise<string> {
    console.log("[API] Get meeting realtime transcript request for meeting:", meeting_id);
    try {
      const params = new URLSearchParams({
        token,
        meeting_id: meeting_id.toString(),
      });

      const response = await axios.get<{ transcript: string }>(
        `${this.baseURL}/meeting_rt_transcript?${params.toString()}`
      );
      console.log("[API] Realtime transcript retrieved for meeting:", meeting_id);
      return response.data.transcript || "";
    } catch (error: any) {
      console.error("[API] Get meeting realtime transcript failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Run Meeting Summary
  async runMeetingSummary(
    token: string,
    meeting_id: number
  ): Promise<void> {
    console.log("[API] Run meeting summary request for meeting:", meeting_id);
    try {
      await axios.post<null>(
        `${this.baseURL}/run_meeting_summary`,
        { token, meeting_id }
      );
      console.log("[API] Meeting summary executed successfully");
    } catch (error: any) {
      console.error("[API] Run meeting summary failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // List prompt files available for test prompts (prompt developers only)
  async getPromptFiles(token: string): Promise<string[]> {
    console.log("[API] Fetching prompt files");
    try {
      const params = new URLSearchParams({ token });
      const response = await axios.get<string[]>(
        `${this.baseURL}/prompt_files?${params.toString()}`
      );
      console.log("[API] Prompt files fetched:", response.data.length);
      return response.data;
    } catch (error: any) {
      console.error("[API] Fetch prompt files failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Run a test prompt on a meeting (result is emailed to the logged-in user)
  async runTestPrompt(payload: RunTestPromptPayload): Promise<RunTestPromptResponse> {
    console.log("[API] Run test prompt for meeting:", payload.meeting_id, "model:", payload.model_name);
    try {
      const response = await axios.post<RunTestPromptResponse>(
        `${this.baseURL}/run_test_prompt`,
        payload,
        { timeout: 30000 }
      );
      console.log("[API] Test prompt started successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Run test prompt failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Update Meeting Comments
  async updateCommentsMeeting(payload: {
    token: string;
    meeting_id: number;
    comments: string;
    source: string;
    meetingtype_id: number;
  }): Promise<void> {
    console.log("[API] Update meeting comments for meeting:", payload.meeting_id);
    try {
      await axios.post<null>(
        `${this.baseURL}/updateCommentsMeeting`,
        payload
      );
      console.log("[API] Meeting comments updated successfully");
    } catch (error: any) {
      console.error("[API] Update meeting comments failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Get Patient History
  async getPatientHistory(
    token: string,
    patientId: string | number
  ): Promise<Array<{ id: number; created_at: number; patient_id: number; date: number; meeting_id: number; user_id: number; action: string; type: string; notes: string }>> {
    console.log("[API] Get patient history request for patient:", patientId);
    try {
      const params = new URLSearchParams({
        token,
        patient_id: patientId.toString(),
      });

      const response = await axios.get<Array<{ id: number; created_at: number; patient_id: number; date: number; meeting_id: number; user_id: number; action: string; type: string; notes: string }>>(
        `${this.baseURL}/getPatientHistory?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} history records for patient ${patientId}`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get patient history failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Send Meeting Link
  async sendMeetingLink(
    token: string,
    meeting_id: number
  ): Promise<{ success: boolean; message?: string }> {
    console.log("[API] Send meeting link request for meeting:", meeting_id);
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(
        `${this.baseURL}/send_meeting_link`,
        { token, meeting_id }
      );
      console.log("[API] Meeting link sent successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Send meeting link failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Get Task Types
  async getTaskTypes(token: string): Promise<TaskType[]> {
    console.log("[API] Get task types request");
    try {
      const params = new URLSearchParams({ token });
      const response = await axios.get<TaskType[]>(
        `${this.baseURL}/task_types?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} task types`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get task types failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Create Internal Task
  async createInternalTask(
    payload: CreateInternalTaskPayload
  ): Promise<CreateInternalTaskResponse> {
    console.log("[API] Create internal task request for patient:", payload.patient_id);
    try {
      const response = await axios.post<CreateInternalTaskResponse>(
        `${this.baseURL}/internal_task`,
        payload
      );
      console.log("[API] Internal task created successfully, task ID:", response.data.id);
      return response.data;
    } catch (error: any) {
      console.error("[API] Create internal task failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Get Internal Tasks
  async getInternalTasks(
    payload: GetInternalTasksPayload
  ): Promise<GetInternalTasksResponse> {
    console.log("[API] Get internal tasks request");
    try {
      const params = new URLSearchParams({
        token: payload.token,
        patient_id: payload.patient_id.toString(),
        user_id: payload.user_id.toString(),
        clinic_id: payload.clinic_id !== undefined ? payload.clinic_id.toString() : '0',
      });

      const response = await axios.get<GetInternalTasksResponse>(
        `${this.baseURL}/internal_task?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} internal tasks`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get internal tasks failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Get a single internal task straight from the server. The tasks list is
  // cached for minutes, so a row handed to a modal can be out of date; anything
  // that writes a status has to refresh through this first.
  async getInternalTaskById(
    payload: GetInternalTaskByIdPayload
  ): Promise<InternalTaskItem> {
    console.log("[API] Get internal task request for task:", payload.id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        id: payload.id.toString(),
        patient_id: payload.patient_id.toString(),
      });

      const response = await axios.get<InternalTaskItem>(
        `${this.baseURL}/internal_task_by_id?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error("[API] Get internal task failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Update Internal Task
  async updateInternalTask(
    payload: UpdateInternalTaskPayload
  ): Promise<UpdateInternalTaskResponse> {
    console.log("[API] Update internal task request for task:", payload.id);
    try {
      const response = await axios.post<UpdateInternalTaskResponse>(
        `${this.baseURL}/update_internal_task`,
        payload
      );
      console.log("[API] Internal task updated successfully, task ID:", response.data.id);
      return response.data;
    } catch (error: any) {
      console.error("[API] Update internal task failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async deleteInternalTask(
    payload: DeleteInternalTaskPayload
  ): Promise<any> {
    console.log("[API] Delete internal task request for task:", payload.id);
    try {
      const response = await axios.post<any>(
        `${this.baseURL}/delete_internal_task`,
        payload
      );
      console.log("[API] Internal task deleted successfully, task ID:", payload.id);
      return response.data;
    } catch (error: any) {
      console.error("[API] Delete internal task failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Get Task History
  async getTaskHistory(
    payload: GetTaskHistoryPayload
  ): Promise<GetTaskHistoryResponse> {
    console.log("[API] Get task history request for task:", payload.task_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        task_id: payload.task_id.toString(),
      });

      const response = await axios.get<GetTaskHistoryResponse>(
        `${this.baseURL}/getTaskHistory?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} history items for task ${payload.task_id}`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get task history failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // ==========================================================================
  // Case Closure (Finish Treatment) Approval
  // ==========================================================================

  async getFinishApprovalData(
    token: string,
    patientId: number
  ): Promise<FinishApprovalData> {
    console.log("[API] Get finish approval data for patient:", patientId);
    try {
      const params = new URLSearchParams({
        token,
        patient_id: patientId.toString(),
      });

      const response = await axios.get<FinishApprovalData>(
        `${this.baseURL}/finish_approval_data?${params.toString()}`,
        { timeout: 30000 }
      );
      console.log("[API] Finish approval data retrieved successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Get finish approval data failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async finishApproval(
    payload: FinishApprovalPayload
  ): Promise<FinishApprovalResponse> {
    console.log("[API] Meeting approval request for patient:", payload.patient_id, {
      save_only: payload.save_only,
      is_formal: payload.is_formal,
    });
    try {
      const response = await axios.post<FinishApprovalResponse>(
        `${this.baseURL}/finish_approval`,
        payload,
        { timeout: 30000 }
      );
      console.log("[API] Meeting approval response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("[API] Meeting approval failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Get Notes
  async getNotes(
    payload: GetNotesPayload
  ): Promise<GetNotesResponse> {
    console.log("[API] Get notes request for object:", payload.object, "ID:", payload.object_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        object: payload.object,
        object_id: payload.object_id.toString(),
      });

      const response = await axios.get<GetNotesResponse>(
        `${this.baseURL}/notes?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data.length} notes`);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get notes failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Create Note
  async createNote(
    payload: CreateNotePayload
  ): Promise<CreateNoteResponse> {
    console.log("[API] Create note request for object:", payload.object, "ID:", payload.object_id);
    try {
      const response = await axios.post<CreateNoteResponse>(
        `${this.baseURL}/note`,
        payload
      );
      console.log("[API] Note created successfully, ID:", response.data.id);
      return response.data;
    } catch (error: any) {
      console.error("[API] Create note failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Update Note
  async updateNote(
    payload: UpdateNotePayload
  ): Promise<UpdateNoteResponse> {
    console.log("[API] Update note request for note ID:", payload.id);
    try {
      const response = await axios.post<UpdateNoteResponse>(
        `${this.baseURL}/update_note`,
        payload
      );
      console.log("[API] Note updated successfully, ID:", response.data.id);
      return response.data;
    } catch (error: any) {
      console.error("[API] Update note failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * Update guardian status for a patient
   * POST /update_guardian_status
   */
  async updateGuardianStatus(
    payload: UpdateGuardianStatusPayload
  ): Promise<UpdateGuardianStatusResponse> {
    console.log("[API] Update guardian status request for patient:", payload.patient_id);
    try {
      const response = await axios.post<UpdateGuardianStatusResponse>(
        `${this.baseURL}/update_guardian_status`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      console.log("[API] Guardian status updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update guardian status failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * Update upload files contacted status for a patient
   * POST /updateUploadFilesContacted
   */
  async callPatient(payload: { token: string; meeting_id: number; phone?: string; call_type?: string }): Promise<void> {
    console.log("[API] Call patient for meeting:", payload.meeting_id);
    try {
      await axios.post(`${this.baseURL}/call_patient`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      console.log("[API] Call patient request successful");
    } catch (error: any) {
      console.error("[API] Call patient failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async callPatientConf(payload: { token: string; patient_id: number; phone?: string; call_type?: string }): Promise<void> {
    console.log("[API] Conference call for patient:", payload.patient_id);
    try {
      const response = await axios.post<{ success?: boolean; error?: string }>(
        `${this.baseURL}/call_patient_conf`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      // Backend returns 200 with {error: "..."} when a phone is missing.
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
      console.log("[API] Conference call request successful");
    } catch (error: any) {
      console.error("[API] Conference call failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      if (error.message && !error.response) {
        throw error;
      }
      throw new Error(error.response?.data?.error || error.response?.data?.message || "Call failed");
    }
  }

  /**
   * Get a Twilio Voice SDK access token for browser WhatsApp calls
   * POST /voice/token
   */
  async getVoiceToken(token: string): Promise<VoiceTokenResponse> {
    console.log("[API] Get voice token");
    try {
      const response = await axios.post<VoiceTokenResponse>(
        `${this.baseURL}/voice/token`,
        { token },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error: any) {
      console.error("[API] Get voice token failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * Latest known WhatsApp call-permission state for the number the patient
   * would be called on (captured from the patient's template reply)
   * POST /voice/whatsapp_call_permission_status
   */
  async getWhatsAppCallPermissionStatus(payload: { token: string; patient_id: number; phone?: string }): Promise<{
    status: "accepted" | "rejected" | "expired" | "unknown" | "requested";
    phone: string;
    updated_at: string | null;
    valid_until: string | null;
    requested_at: string | null;
  }> {
    console.log("[API] Get WhatsApp call permission status for patient:", payload.patient_id);
    try {
      const response = await axios.post(
        `${this.baseURL}/voice/whatsapp_call_permission_status`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error: any) {
      console.error("[API] Get WhatsApp call permission status failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * Cancel a still-ringing WhatsApp call leg after the browser hung up
   * POST /voice/cancel_whatsapp_call
   */
  async cancelWhatsAppCall(payload: { token: string; patient_id: number; phone?: string }): Promise<{ canceled: number }> {
    console.log("[API] Cancel ringing WhatsApp call for patient:", payload.patient_id);
    try {
      const response = await axios.post<{ canceled: number }>(
        `${this.baseURL}/voice/cancel_whatsapp_call`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error: any) {
      console.error("[API] Cancel WhatsApp call failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * Send the WhatsApp call-permission template to a patient
   * POST /voice/whatsapp_call_permission
   */
  async requestWhatsAppCallPermission(
    payload: WhatsAppCallPermissionPayload
  ): Promise<WhatsAppCallPermissionResponse> {
    console.log("[API] Request WhatsApp call permission for patient:", payload.patient_id);
    try {
      const response = await axios.post<WhatsAppCallPermissionResponse>(
        `${this.baseURL}/voice/whatsapp_call_permission`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
      return response.data;
    } catch (error: any) {
      console.error("[API] WhatsApp call permission request failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getNumberOfTasksNeedAction(payload: { token: string }): Promise<{ result: number }> {
    console.log("[API] Get number of tasks needing action");
    try {
      const response = await axios.get<{ result: number }>(
        `${this.baseURL}/number_of_tasks_need_action?token=${payload.token}`
      );
      console.log("[API] Tasks needing action:", response.data.result);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get number of tasks needing action failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async updateUploadFilesContacted(
    payload: UpdateUploadFilesContactedPayload
  ): Promise<UpdateUploadFilesContactedResponse> {
    console.log("[API] Update upload files contacted request for patient:", payload.patient_id);
    try {
      const response = await axios.post<UpdateUploadFilesContactedResponse>(
        `${this.baseURL}/updateUploadFilesContacted`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      console.log("[API] Upload files contacted updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Update upload files contacted failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // ── WhatsApp Messaging ──
  // Set REACT_APP_WA_MOCK=true in .env.development to use local mock data.

  async getWhatsAppConversations(payload: GetWhatsAppConversationsPayload): Promise<GetWhatsAppConversationsResponse> {
    console.log("[API] getWhatsAppConversations", payload.search, payload.status);
    try {
      const params: Record<string, any> = { token: payload.token, page: payload.page ?? 1, limit: payload.limit ?? 20 };
      if (payload.search) params.search = payload.search;
      if (payload.status) params.status = payload.status;
      const res = await axios.get<GetWhatsAppConversationsResponse>(`${this.baseURL}/whatsapp/conversations`, { params });
      return res.data;
    } catch (error: any) {
      console.error("[API] getWhatsAppConversations failed", error.response?.status, error.message);
      throw error;
    }
  }

  async assignWhatsAppConversation(payload: AssignWhatsAppConversationPayload): Promise<{ conversation: WhatsAppConversation }> {
    console.log("[API] assignWhatsAppConversation", payload.phone_number, payload.user_name);
    try {
      const res = await axios.patch<{ conversation: WhatsAppConversation }>(`${this.baseURL}/whatsapp/conversations/assign`, payload);
      return res.data;
    } catch (error: any) {
      console.error("[API] assignWhatsAppConversation failed", error.response?.status, error.message);
      throw error;
    }
  }

  async updateWhatsAppConversationStatus(payload: UpdateWhatsAppConversationStatusPayload): Promise<{ conversation: WhatsAppConversation }> {
    console.log("[API] updateWhatsAppConversationStatus", payload.phone_number, payload.status);
    try {
      const res = await axios.patch<{ conversation: WhatsAppConversation }>(`${this.baseURL}/whatsapp/conversations/status`, payload);
      return res.data;
    } catch (error: any) {
      console.error("[API] updateWhatsAppConversationStatus failed", error.response?.status, error.message);
      throw error;
    }
  }

  async getWhatsAppMessages(payload: GetWhatsAppMessagesPayload): Promise<GetWhatsAppMessagesResponse> {
    console.log("[API] getWhatsAppMessages phone", payload.phone_number, "page", payload.page);
    try {
      const params: Record<string, any> = { token: payload.token, phone_number: payload.phone_number, page: payload.page ?? 1, limit: payload.limit ?? 50 };
      const res = await axios.get<GetWhatsAppMessagesResponse>(`${this.baseURL}/whatsapp/messages`, { params });
      return res.data;
    } catch (error: any) {
      console.error("[API] getWhatsAppMessages failed", error.response?.status, error.message);
      throw error;
    }
  }

  async uploadWhatsAppMedia(token: string, file: File): Promise<{ url: string; content_type: string; filename: string }> {
    console.log("[API] uploadWhatsAppMedia", file.name, file.type);
    const form = new FormData();
    form.append("token", token);
    form.append("file", file);
    try {
      const res = await axios.post(`${this.baseURL}/whatsapp/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (error: any) {
      console.error("[API] uploadWhatsAppMedia failed", error.response?.status, error.message);
      throw error;
    }
  }

  async sendWhatsAppMessage(payload: SendWhatsAppMessagePayload): Promise<SendWhatsAppMessageResponse> {
    console.log("[API] sendWhatsAppMessage", payload.phone_number, (payload.body || "").slice(0, 30));
    try {
      const res = await axios.post<SendWhatsAppMessageResponse>(`${this.baseURL}/whatsapp/messages`, payload);
      return res.data;
    } catch (error: any) {
      console.error("[API] sendWhatsAppMessage failed", error.response?.status, error.message);
      throw error;
    }
  }

  async markWhatsAppRead(payload: MarkWhatsAppReadPayload): Promise<void> {
    console.log("[API] markWhatsAppRead phone", payload.phone_number);
    try {
      await axios.post(`${this.baseURL}/whatsapp/mark-read`, payload);
    } catch (error: any) {
      console.error("[API] markWhatsAppRead failed", error.response?.status, error.message);
      throw error;
    }
  }

  async getWhatsAppTemplates(token: string): Promise<WhatsAppTemplate[]> {
    console.log("[API] getWhatsAppTemplates");
    try {
      const res = await axios.get<WhatsAppTemplate[]>(`${this.baseURL}/whatsapp/templates`, { params: { token } });
      return res.data;
    } catch (error: any) {
      console.error("[API] getWhatsAppTemplates failed", error.response?.status, error.message);
      throw error;
    }
  }

  async getPatientsByPhone(token: string, phoneNumber: string): Promise<{ patients: Array<{ id: number; name: string; hm_id?: string; created_at?: string }> }> {
    console.log("[API] getPatientsByPhone", phoneNumber);
    try {
      const res = await axios.get(`${this.baseURL}/whatsapp/patients-by-phone`, { params: { token, phone_number: phoneNumber } });
      return res.data;
    } catch (error: any) {
      console.error("[API] getPatientsByPhone failed", error.response?.status, error.message);
      throw error;
    }
  }

  async getWhatsAppUnreadCount(payload: { token: string }): Promise<{ count: number }> {
    console.log("[API] getWhatsAppUnreadCount");
    try {
      const res = await axios.get<{ count: number }>(`${this.baseURL}/whatsapp/unread-count`, { params: { token: payload.token } });
      return res.data;
    } catch (error: any) {
      console.error("[API] getWhatsAppUnreadCount failed", error.response?.status, error.message);
      throw error;
    }
  }

  async getWhatsAppConversationByPatientId(token: string, patientId: number): Promise<WhatsAppConversation | null> {
    console.log("[API] getWhatsAppConversationByPatientId", patientId);
    try {
      const res = await this.getWhatsAppConversations({ token, limit: 200 });
      return res.conversations.find((c) => c.patient_id === patientId) || null;
    } catch {
      return null;
    }
  }

  async redFlagRequest(payload: import('../types/api').RedFlagRequestPayload): Promise<void> {
    console.log("[API] Red flag request for patient:", payload.patient_id, "remove:", payload.remove_flag);
    try {
      await axios.post(`${this.baseURL}/red_flag_request`, payload);
      console.log("[API] Red flag request successful");
    } catch (error: any) {
      console.error("[API] Red flag request failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async uploadClalitExcel(
    payload: import('../types/api').UploadClalitExcelPayload
  ): Promise<import('../types/api').UploadClalitExcelResponse> {
    console.log("[API] Upload Clalit Excel:", payload.filename);
    try {
      const response = await axios.post<import('../types/api').UploadClalitExcelResponse>(
        `${this.baseURL}/upload_clalit_excel`,
        payload
      );
      console.log("[API] Clalit Excel uploaded successfully");
      return response.data;
    } catch (error: any) {
      console.error("[API] Upload Clalit Excel error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async runClalitBilling(
    payload: import('../types/api').RunBillingPayload
  ): Promise<import('../types/api').RunBillingResponse> {
    console.log("[API] Run Clalit Billing:", payload.year, payload.month);
    try {
      const response = await axios.post<import('../types/api').RunBillingResponse>(
        `${this.baseURL}/run_clalit_billing`,
        payload
      );
      console.log("[API] Run Clalit Billing enqueued");
      return response.data;
    } catch (error: any) {
      console.error("[API] Run Clalit Billing error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async runMaccabiBilling(
    payload: import('../types/api').RunBillingPayload
  ): Promise<import('../types/api').RunBillingResponse> {
    console.log("[API] Run Maccabi Billing:", payload.year, payload.month);
    try {
      const response = await axios.post<import('../types/api').RunBillingResponse>(
        `${this.baseURL}/run_maccabi_billing`,
        payload
      );
      console.log("[API] Run Maccabi Billing enqueued");
      return response.data;
    } catch (error: any) {
      console.error("[API] Run Maccabi Billing error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getAvailableBillingMonths(
    payload: import('../types/api').AvailableBillingMonthsPayload
  ): Promise<import('../types/api').AvailableBillingMonthsResponse> {
    console.log("[API] Available Billing Months:", payload.institution);
    try {
      const response = await axios.post<import('../types/api').AvailableBillingMonthsResponse>(
        `${this.baseURL}/available_billing_months`,
        payload
      );
      console.log("[API] Available Billing Months result:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("[API] Available Billing Months error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async loadClalitRejects(
    payload: import('../types/api').LoadClalitRejectsPayload
  ): Promise<import('../types/api').LoadClalitRejectsResponse> {
    console.log("[API] Load Clalit Rejects:", payload.filename, payload.year, payload.month);
    try {
      const response = await axios.post<import('../types/api').LoadClalitRejectsResponse>(
        `${this.baseURL}/load_clalit_rejects`,
        payload
      );
      console.log("[API] Load Clalit Rejects result:", response.data?.success);
      return response.data;
    } catch (error: any) {
      console.error("[API] Load Clalit Rejects error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getMobileTrackingEvents(params: {
    token: string;
    patient_id: number;
    page?: number;
    limit?: number;
    sort_column?: string;
    sort_direction?: string;
    filters?: string;
  }): Promise<{ events: any[]; total: number; page: number; limit: number }> {
    const { token, patient_id, page = 1, limit = 50, sort_column = 'created_at', sort_direction = 'DESC', filters } = params;
    const queryParams: Record<string, any> = { token, patient_id, page, limit, sort_column, sort_direction };
    if (filters) queryParams.filters = filters;
    const response = await axios.get(`${this.baseURL}/mobile_tracking_history`, {
      params: queryParams,
    });
    return response.data;
  }

  getMobileTrackingSSEUrl(token: string, patient_id: number): string {
    return `${this.baseURL}/mobile_tracking/events?token=${encodeURIComponent(token)}&patient_id=${patient_id}`;
  }

  // ==========================================================================
  // Real-time Notifications (notification center / toast)
  // ==========================================================================

  // Returns all non-removed notifications for the user/type within the backend's
  // retention window. The frontend displays exactly what is returned.
  async getUserNotifications(
    payload: GetUserNotificationsPayload
  ): Promise<GetUserNotificationsResponse> {
    const params = new URLSearchParams({
      token: payload.token,
      user_id: payload.user_id.toString(),
      notification_type: payload.notification_type,
    });
    const response = await axios.get<GetUserNotificationsResponse>(
      `${this.baseURL}/get_user_notifications?${params.toString()}`
    );
    return response.data;
  }

  async markNotificationAsRead(
    payload: MarkNotificationAsReadPayload
  ): Promise<void> {
    await axios.post(`${this.baseURL}/mark_notifications_as_read`, {
      token: payload.token,
      user_id: payload.user_id,
      notification_id: payload.notification_id,
    });
  }

  async removeNotifications(
    payload: RemoveNotificationsPayload
  ): Promise<void> {
    await axios.post(`${this.baseURL}/remove_notifications`, {
      token: payload.token,
      user_id: payload.user_id,
    });
  }

  // App - mobile patient-generated data
  async getPatientMoodStatus(
    payload: GetPatientMoodStatusPayload
  ): Promise<GetPatientMoodStatusResponse> {
    console.log("[API] Get patient mood status request for patient:", payload.patient_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        patient_id: payload.patient_id.toString(),
      });

      const response = await axios.get<GetPatientMoodStatusResponse>(
        `${this.baseURL}/get_patient_mood_status?${params.toString()}`,
        { timeout: 30000 }
      );
      console.log("[API] Patient mood status retrieved successfully");
      return response.data || [];
    } catch (error: any) {
      console.error("[API] Get patient mood status failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Create a medical opinion (מכתב לגורם חיצוני) request. Files travel as base64 in the body, so
  // the request can be tens of MB - the default axios timeout is left in place and the
  // caller shows a spinner. A business-rule rejection comes back as
  // {status: "error", message} with HTTP 200; a 403 throws.
  async createMedicalOpinion(
    payload: CreateMedicalOpinionPayload
  ): Promise<CreateMedicalOpinionResponse> {
    console.log("[API] Create medical opinion request for patient:", payload.patient_id);
    try {
      const response = await axios.post<CreateMedicalOpinionResponse>(
        `${this.baseURL}/medical_opinion`,
        payload
      );
      console.log("[API] Create medical opinion returned:", response.data?.status);
      return response.data;
    } catch (error: any) {
      console.error("[API] Create medical opinion failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getMedicalOpinions(
    payload: GetMedicalOpinionsPayload
  ): Promise<GetMedicalOpinionsResponse> {
    console.log("[API] Get medical opinions for patient:", payload.patient_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        patient_id: payload.patient_id.toString(),
      });
      const response = await axios.get<GetMedicalOpinionsResponse>(
        `${this.baseURL}/medical_opinions?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data?.length ?? 0} medical opinions`);
      return response.data || [];
    } catch (error: any) {
      console.error("[API] Get medical opinions failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // One request by id, with its files. Used where the caller already knows which request it
  // wants, instead of pulling the patient's whole list and filtering.
  async getMedicalOpinion(
    payload: GetMedicalOpinionPayload
  ): Promise<GetMedicalOpinionResponse> {
    console.log("[API] Get medical opinion:", payload.medical_opinion_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        medical_opinion_id: payload.medical_opinion_id.toString(),
      });
      const response = await axios.get<GetMedicalOpinionResponse>(
        `${this.baseURL}/medical_opinion?${params.toString()}`
      );
      console.log("[API] Medical opinion retrieved, status:", response.data?.status);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get medical opinion failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Fetch one attachment as base64, on demand when the user selects it for preview.
  async getMedicalOpinionFile(
    payload: GetMedicalOpinionFilePayload
  ): Promise<GetMedicalOpinionFileResponse> {
    console.log("[API] Get medical opinion file:", payload.file_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        patient_id: payload.patient_id.toString(),
        medical_opinion_id: payload.medical_opinion_id.toString(),
        file_id: payload.file_id,
      });
      const response = await axios.get<GetMedicalOpinionFileResponse>(
        `${this.baseURL}/medical_opinion_file?${params.toString()}`
      );
      console.log("[API] Medical opinion file retrieved:", response.data?.filename);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get medical opinion file failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Approve or reject the obligation on a pending request. Admin only on the backend.
  async medicalOpinionObligationResponse(
    payload: MedicalOpinionObligationResponsePayload
  ): Promise<MedicalOpinionStatusResponse> {
    console.log("[API] Medical opinion obligation response:", {
      medical_opinion_id: payload.medical_opinion_id,
      is_approved: payload.is_approved,
    });
    try {
      const response = await axios.post<MedicalOpinionStatusResponse>(
        `${this.baseURL}/medical_opinion_obligation_response`,
        payload
      );
      console.log("[API] Obligation response returned:", response.data?.status);
      return response.data;
    } catch (error: any) {
      console.error("[API] Medical opinion obligation response failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // The opinion type dropdown, with its Hebrew labels. Static clinic vocabulary served from
  // the backend so the labels are not duplicated here - see useMedicalOpinionOptions for the
  // caching.
  async getMedicalOpinionTypes(token: string): Promise<GetMedicalOpinionTypesResponse> {
    console.log("[API] Get medical opinion types");
    try {
      const params = new URLSearchParams({ token });
      const response = await axios.get<GetMedicalOpinionTypesResponse>(
        `${this.baseURL}/medical_opinion_types?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data?.length ?? 0} medical opinion types`);
      return response.data || [];
    } catch (error: any) {
      console.error("[API] Get medical opinion types failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // The close reason dropdown. Each option carries requires_notes, so the client does not
  // hardcode which reason needs free text.
  async getMedicalOpinionCloseReasons(
    token: string
  ): Promise<GetMedicalOpinionCloseReasonsResponse> {
    console.log("[API] Get medical opinion close reasons");
    try {
      const params = new URLSearchParams({ token });
      const response = await axios.get<GetMedicalOpinionCloseReasonsResponse>(
        `${this.baseURL}/medical_opinion_close_reasons?${params.toString()}`
      );
      console.log(`[API] Retrieved ${response.data?.length ?? 0} medical opinion close reasons`);
      return response.data || [];
    } catch (error: any) {
      console.error("[API] Get medical opinion close reasons failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // The letter's content, for the Psychiatric Opinion Letter modal. Seeded from the
  // patient's last psychiatric meeting until something has been saved against the request.
  async getMedicalOpinionPsyData(
    payload: GetMedicalOpinionPsyDataPayload
  ): Promise<MedicalOpinionPsyData> {
    console.log("[API] Get medical opinion psy data:", payload.medical_opinion_id);
    try {
      const params = new URLSearchParams({
        token: payload.token,
        medical_opinion_id: payload.medical_opinion_id.toString(),
      });
      const response = await axios.get<MedicalOpinionPsyData>(
        `${this.baseURL}/medical_opinion_psy_data?${params.toString()}`
      );
      console.log("[API] Psy data retrieved, seeded:", response.data?.is_seeded);
      return response.data;
    } catch (error: any) {
      console.error("[API] Get medical opinion psy data failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Save the letter (is_completed false, refused while locked) or complete the request
  // (is_completed true, refused unless locked). Assigned doctor only on the backend.
  async medicalOpinionPsyResponse(
    payload: MedicalOpinionPsyResponsePayload
  ): Promise<MedicalOpinionStatusResponse> {
    console.log("[API] Medical opinion psy response:", {
      medical_opinion_id: payload.medical_opinion_id,
      is_completed: payload.is_completed,
    });
    try {
      const response = await axios.post<MedicalOpinionStatusResponse>(
        `${this.baseURL}/medical_opinion_psy_response`,
        payload
      );
      console.log("[API] Psy response returned:", response.data?.status);
      return response.data;
    } catch (error: any) {
      console.error("[API] Medical opinion psy response failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Create PDF: saves the content, renders the letter, stores it as the request's single
  // output_summary file and locks the content to it.
  async postMedicalOpinionPdf(
    payload: MedicalOpinionPsyContentPayload
  ): Promise<MedicalOpinionPdfResponse> {
    console.log("[API] Create medical opinion pdf:", payload.medical_opinion_id);
    try {
      const response = await axios.post<MedicalOpinionPdfResponse>(
        `${this.baseURL}/medical_opinion_pdf`,
        payload
      );
      console.log("[API] Medical opinion pdf returned:", response.data?.status);
      return response.data;
    } catch (error: any) {
      console.error("[API] Create medical opinion pdf failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Attach one file to an existing request, always as output_other. Assigned doctor only,
  // and refused while the letter is locked.
  async addMedicalOpinionFile(
    payload: AddMedicalOpinionFilePayload
  ): Promise<AddMedicalOpinionFileResponse> {
    console.log("[API] Add medical opinion file:", payload.filename);
    try {
      const response = await axios.post<AddMedicalOpinionFileResponse>(
        `${this.baseURL}/medical_opinion_file`,
        payload
      );
      console.log("[API] Add medical opinion file returned:", response.data?.status);
      return response.data;
    } catch (error: any) {
      console.error("[API] Add medical opinion file failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Remove one output_other attachment from a request.
  async deleteMedicalOpinionFile(
    payload: DeleteMedicalOpinionFilePayload
  ): Promise<MedicalOpinionStatusResponse> {
    console.log("[API] Delete medical opinion file:", payload.file_id);
    try {
      const response = await axios.post<MedicalOpinionStatusResponse>(
        `${this.baseURL}/medical_opinion_file_delete`,
        payload
      );
      console.log("[API] Delete medical opinion file returned:", response.data?.status);
      return response.data;
    } catch (error: any) {
      console.error("[API] Delete medical opinion file failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // Reopen a locked letter for editing. The pdf it was locked to is discarded with it - it
  // has to be generated again before the request can be completed. The stored pdf stays until the next generation
  // replaces it, but the request cannot be completed until then.
  async medicalOpinionUnlock(
    payload: MedicalOpinionUnlockPayload
  ): Promise<MedicalOpinionStatusResponse> {
    console.log("[API] Medical opinion unlock:", payload.medical_opinion_id);
    try {
      const response = await axios.post<MedicalOpinionStatusResponse>(
        `${this.baseURL}/medical_opinion_unlock`,
        payload
      );
      console.log("[API] Medical opinion unlock returned:", response.data?.status);
      return response.data;
    } catch (error: any) {
      console.error("[API] Medical opinion unlock failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  // ==========================================================================
  // AI Support
  //
  // The message endpoint returns 202 and the answer arrives over SSE
  // (useAISupportSSE), so nothing here waits on the model.
  // ==========================================================================

  async startAISupportSession(payload: AISupportStartPayload): Promise<AISupportStartResponse> {
    console.log("[API] AI Support: starting session", { lang: payload.lang });
    try {
      const response = await axios.post<AISupportStartResponse>(
        `${this.baseURL}/ai_support/session`,
        payload
      );
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support start session failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async sendAISupportMessage(payload: AISupportMessagePayload): Promise<{ status: string }> {
    console.log("[API] AI Support: sending message", { session_id: payload.session_id });
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/message`, payload);
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support send message failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async sendAISupportFeedback(payload: AISupportFeedbackPayload): Promise<{ status: string }> {
    console.log("[API] AI Support: feedback", payload.outcome);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/feedback`, payload);
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support feedback failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /** What the therapist wrote about a conversation, in their own words. Feeds
   *  the knowledge base, so it is sent separately from the verdict rating. */
  async sendAISupportFeedbackNote(
    token: string, sessionId: number, note: string
  ): Promise<{ status: string }> {
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/feedback_note`, {
        token,
        session_id: sessionId,
        note,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support feedback note failed", {
        status: error.response?.status,
        data: error.response?.data,
      });
      throw error;
    }
  }

  async escalateAISupport(token: string, sessionId: number, reason?: string): Promise<{ status: string }> {
    console.log("[API] AI Support: escalating", sessionId);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/escalate`, {
        token,
        session_id: sessionId,
        reason,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support escalate failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getAISupportSession(token: string, sessionId: number): Promise<AISupportSessionDetail> {
    console.log("[API] AI Support: loading session", sessionId);
    try {
      const response = await axios.get<AISupportSessionDetail>(
        `${this.baseURL}/ai_support/session/${sessionId}?token=${token}`
      );
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support get session failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getAISupportSessions(
    token: string,
    filters: {
      status?: string;
      outcome?: string;
      limit?: number;
      /** Sessions opened by support admins — the team exercising the assistant. */
      include_test?: boolean;
    } = {}
  ): Promise<AISupportSessionsResponse> {
    console.log("[API] AI Support: listing sessions", filters);
    try {
      const response = await axios.get<AISupportSessionsResponse>(
        `${this.baseURL}/ai_support/sessions`,
        { params: { token, ...filters } }
      );
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support list sessions failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * One conversation the caller left open, to ask them to finish. A POST
   * because the server records the reminder as it hands it out -- fetching
   * without recording would re-ask on every sign-in forever.
   */
  async takeAISupportCloseReminder(
    token: string
  ): Promise<{ session: AISupportSessionSummary | null }> {
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/close_reminder`, {
        token,
      });
      return response.data;
    } catch (error: any) {
      // A reminder is a nicety; never let it break signing in.
      console.warn("[API] AI Support close reminder failed", {
        status: error.response?.status,
        message: error.message,
      });
      return { session: null };
    }
  }

  async decideAISupportAction(
    token: string,
    actionId: number,
    decision: "approve" | "deny",
    note?: string
  ): Promise<{ status: string }> {
    console.log("[API] AI Support: deciding action", { actionId, decision });
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/approve`, {
        token,
        action_id: actionId,
        decision,
        note,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support decide action failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getMyAISupportSessions(token: string, limit = 25): Promise<AISupportSessionsResponse> {
    console.log("[API] AI Support: loading my past conversations");
    try {
      const response = await axios.get<AISupportSessionsResponse>(
        `${this.baseURL}/ai_support/my_sessions`,
        { params: { token, limit } }
      );
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support my sessions failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async sendAISupportReply(
    token: string,
    sessionId: number,
    message: string
  ): Promise<{ status: string; by: string }> {
    console.log("[API] AI Support: replying to therapist in session", sessionId);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/reply`, {
        token,
        session_id: sessionId,
        message,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support reply failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getAISupportActions(token: string): Promise<{ actions: AISupportAction[] }> {
    console.log("[API] AI Support: loading available actions");
    try {
      const response = await axios.get(`${this.baseURL}/ai_support/actions`, {
        params: { token },
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support actions failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async runAISupportAction(
    token: string,
    sessionId: number,
    action: string,
    args: Record<string, unknown>
  ): Promise<{ action_id: number; ok: boolean; result: unknown }> {
    console.log("[API] AI Support: running action", action, args);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/run_action`, {
        token,
        session_id: sessionId,
        action,
        arguments: args,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support run action failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async getAISupportKB(token: string, status?: string): Promise<AISupportKBResponse> {
    console.log("[API] AI Support: loading knowledge base", status || "all");
    try {
      const response = await axios.get<AISupportKBResponse>(`${this.baseURL}/ai_support/kb`, {
        params: { token, status },
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support KB list failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async saveAISupportKBEntry(
    payload: { token: string } & Partial<AISupportKBEntry>
  ): Promise<{ entry: AISupportKBEntry }> {
    console.log("[API] AI Support: saving KB entry", payload.id, payload.status);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/kb`, payload);
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support KB save failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /** Changes distillation proposes to entries that already exist. */
  async getAISupportKBRevisions(
    token: string
  ): Promise<{
    revisions: AISupportKBRevision[];
    entries: Record<string, AISupportKBEntry | null>;
  }> {
    try {
      const response = await axios.get(`${this.baseURL}/ai_support/kb/revisions`, {
        params: { token, status: "pending" },
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support KB revisions failed", error.message);
      throw error;
    }
  }

  /** Fold a proposed revision into its entry, or reject it. */
  async decideAISupportKBRevision(
    token: string,
    revisionId: number,
    accept: boolean
  ): Promise<{ id: number; status: string; entry_id: number }> {
    console.log("[API] AI Support: deciding KB revision", revisionId, accept);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/kb/revisions`, {
        token,
        revision_id: revisionId,
        accept,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support KB revision decide failed", {
        status: error.response?.status,
        data: error.response?.data,
      });
      throw error;
    }
  }

  /** Destroy an archived knowledge entry. Archived only, and irreversible. */
  async deleteAISupportKBEntry(
    token: string,
    entryId: number
  ): Promise<{ id: number; deleted: boolean }> {
    console.log("[API] AI Support: deleting KB entry", entryId);
    try {
      const response = await axios.delete(`${this.baseURL}/ai_support/kb`, {
        params: { token, entry_id: entryId },
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support KB delete failed", {
        status: error.response?.status,
        data: error.response?.data,
      });
      throw error;
    }
  }

  /**
   * Settle what a conversation actually was. Support only — the assistant
   * proposes a class but may not decide between defect and intervention.
   */
  async classifyAISupportSession(
    token: string,
    sessionId: number,
    issueClass: string,
    causeCode?: string,
    causeNote?: string
  ): Promise<{ issue_class: string }> {
    console.log("[API] AI Support: classifying session", sessionId, issueClass);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/classify`, {
        token,
        session_id: sessionId,
        issue_class: issueClass,
        cause_code: causeCode,
        cause_note: causeNote,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support classify failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * The rules the assistant reads out of the product's source. Re-derived on
   * every request, so what is shown is what the assistant would use right now.
   */
  async getAISupportDerived(
    token: string,
    area?: string
  ): Promise<{ areas: string[]; derived?: AISupportDerived }> {
    try {
      const response = await axios.get(`${this.baseURL}/ai_support/derived`, {
        params: { token, area },
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support derived rules failed", {
        status: error.response?.status,
        data: error.response?.data,
      });
      throw error;
    }
  }

  /**
   * The defect register. `forCause` offers what a new report might attach to,
   * rather than opening a second record for the same fault.
   */
  async getAISupportDefects(
    token: string,
    opts: { status?: string; for_cause?: string; defect_id?: number } = {}
  ): Promise<{ defects?: AISupportDefect[]; defect?: AISupportDefect; sessions?: AISupportSessionSummary[] }> {
    try {
      const response = await axios.get(`${this.baseURL}/ai_support/defects`, {
        params: { token, ...opts },
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support defects failed", error.message);
      throw error;
    }
  }

  /** Open a defect, attach a session to one, or detach it. Support only. */
  async linkAISupportDefect(
    token: string,
    opts: {
      session_id?: number;
      defect_id?: number | null;
      title?: string;
      symptom?: string;
      cause_code?: string;
    }
  ): Promise<{ defect: AISupportDefect | null }> {
    console.log("[API] AI Support: linking defect", opts);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/defects`, {
        token,
        ...opts,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support defect link failed", {
        status: error.response?.status,
        data: error.response?.data,
      });
      throw error;
    }
  }

  /** Change a defect's status, title, tracker reference or notes. */
  async updateAISupportDefect(
    token: string,
    defectId: number,
    fields: { status?: string; title?: string; external_ref?: string; notes?: string }
  ): Promise<{ defect: AISupportDefect }> {
    try {
      const response = await axios.patch(`${this.baseURL}/ai_support/defects`, {
        token,
        defect_id: defectId,
        ...fields,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support defect update failed", error.message);
      throw error;
    }
  }

  /** The class/cause vocabulary and the current split. Support only. */
  async getAISupportClasses(token: string, includeTest = true): Promise<{
    cause_codes: Record<string, string[]>;
    stats: { decided: Record<string, number>; awaiting_decision: Record<string, number>; causes: Record<string, number> };
    awaiting: number[];
  }> {
    try {
      const response = await axios.get(`${this.baseURL}/ai_support/classes`, {
        params: { token, include_test: includeTest },
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support classes failed", error.message);
      throw error;
    }
  }

  /** Close a session a therapist never came back to. Support only. */
  async closeAISupportSession(
    token: string,
    sessionId: number,
    note?: string
  ): Promise<{ status: string }> {
    console.log("[API] AI Support: closing session", sessionId);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/close`, {
        token,
        session_id: sessionId,
        note,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support close failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

  async sendAISupportGuidance(
    token: string,
    sessionId: number,
    guidance: string
  ): Promise<{ status: string }> {
    console.log("[API] AI Support: sending guidance to session", sessionId);
    try {
      const response = await axios.post(`${this.baseURL}/ai_support/guidance`, {
        token,
        session_id: sessionId,
        guidance,
      });
      return response.data;
    } catch (error: any) {
      console.error("[API] AI Support guidance failed", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  }

}


const apiService = new ApiService();
export default apiService;
